import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCognito } from "../../contexts/CognitoProvider";
import { useConfig } from "../../contexts/ConfigContext";
import { useMyAccountData } from "../../hooks/useMyAccountData";
import {
    signOut,
    UserProfile
} from "../../services/authService";
import { formatSectorLabel } from "../../utils/sectorLabels";
import { ConnectionIndicator } from "../common/ConnectionIndicator";
import { ConnectionStatusBanner } from "../common/ConnectionStatusBanner";
import "./PortalHeader.css";

const PortalHeader: React.FC<{ onLogout?: () => void }> = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const config = useConfig();
  const { logout: cognitoLogout } = useCognito();
  const { accountData, currentUser } = useMyAccountData();
  const enabledTabs = config.navigationTabs
    .filter((tab) => tab.enabled)
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    const signedInName = currentUser.displayName;
    setUserProfile({
      id: currentUser.userId || "unknown",
      email: currentUser.email,
      contactPerson: signedInName,
      firmName: accountData?.organisation || config.companyName,
      firmType: "other",
      abn: accountData?.abn || "",
      phone: "",
    });
  }, [currentUser, config.companyName, accountData]);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    cognitoLogout(); // clears in-memory tokens → isAuthenticated becomes false
    signOut(); // clears cookies
    if (onLogout) onLogout();
    navigate("/login");
  };

  const getFirmTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      accounting: "Accounting Firm",
      "real-estate": "Real Estate Agency",
      legal: "Legal Firm",
      compliance: "Compliance Officer",
      other: "Organization",
    };
    return labels[type] || type;
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
    <header className="portal-header">
      <div className="portal-header-container">
        <Link to="/portal/dashboard" className="portal-logo-icon">
          <img
            src={"/arcaml_logo_white_transparent.png"}
            alt="Logo"
            className="logo-image"
          />
        </Link>
        <nav className="portal-nav">
          {enabledTabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.route}
              className={`nav-link ${isActive(tab.route) ? "active" : ""}`}
            >
              {tab.label}
              {tab.id === "faqs" && (
                <span className="faq-offer-badge">
                  <span className="badge-text">Learn Now!</span>
                </span>
              )}
            </Link>
          ))}
        </nav>
        <div className="portal-header-actions">
          <ConnectionIndicator />
          <div className="user-menu">
            <button className="user-btn" data-testid="user-menu-btn">
              <span className="user-avatar">
                {userProfile ? getInitials(userProfile.contactPerson) : "J"}
              </span>
              <span
                className="user-name"
                data-testid="signed-in-user-name"
                title={currentUser.email || undefined}
              >
                {userProfile?.contactPerson || "User"}
              </span>
            </button>
            <div className="dropdown-menu">
              <div className="dropdown-header">
                <div className="profile-info">
                  <div className="profile-name">
                    {userProfile?.contactPerson || "User"}
                  </div>
                  <div className="profile-firm-label">Organisation</div>
                  <div className="profile-firm">
                    {userProfile?.firmName || "Not set"}
                  </div>
                  {accountData?.services && (
                    <div className="profile-type">
                      {formatSectorLabel(accountData.services)}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="logout-btn"
                data-testid="btn-sign-out"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
    <ConnectionStatusBanner />
    </>
  );
};

export default PortalHeader;
